import { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { useGetEtlDailyRollupQuery } from 'src/redux/operations/operations.endpoints';
import { EtlHealthStripV2 } from './etl-health-strip-v2';
import { MartVerificationPanelV2 } from './mart-verification-panel-v2';
import { PlatformReconciliationPanelV2 } from './platform-reconciliation-panel-v2';

const HEALTH_DAYS = 30;

function getErrorMessage(error: unknown): string {
  return (error as { message?: string })?.message ?? 'An unexpected error occurred.';
}

export function EtlHealthDashboardV2() {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [detailRefreshToken, setDetailRefreshToken] = useState(0);

  const queryParams = { days: HEALTH_DAYS, ...(refresh ? { refresh: 1 as const } : {}) };

  const rollupQuery = useGetEtlDailyRollupQuery(queryParams);

  const isLoading = rollupQuery.isLoading;
  const isFetching = rollupQuery.isFetching || refresh;
  const isError = rollupQuery.isError;
  const error = rollupQuery.error;

  const handleRefresh = useCallback(async () => {
    setRefresh(true);
    setDetailRefreshToken((current) => current + 1);
    try {
      await rollupQuery.refetch();
    } finally {
      setRefresh(false);
    }
  }, [rollupQuery]);

  return (
    <div className="flex flex-col gap-6">
      <PortalDsCard aria-labelledby="etl-health-heading-v2">
        <PortalDsSectionHeader
          overline="Data quality"
          title="ETL health"
          description={`Last ${HEALTH_DAYS} days of data quality checks across platform reconciliation and mart verification.`}
          titleId="etl-health-heading-v2"
          actions={(
            <>
              {selectedDate ? (
                <button
                  type="button"
                  className={portalDs.link}
                  onClick={() => setSelectedDate(null)}
                >
                  Clear day filter (
                  {selectedDate}
                  )
                </button>
              ) : null}
              <PortalDsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleRefresh()}
                disabled={isFetching}
              >
                Refresh
              </PortalDsButton>
            </>
          )}
        />

        {(isLoading || isFetching) && (
          <div className={cn('mb-4 flex items-center gap-2', portalDs.bodySm)} role="status">
            <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
            Loading ETL health data…
          </div>
        )}

        {isError && !isLoading && (
          <PortalDsAlert variant="danger" title="Could not load ETL health dashboard">
            {getErrorMessage(error)}
          </PortalDsAlert>
        )}

        {!isLoading && !isError && rollupQuery.data?.rollup?.length ? (
          <EtlHealthStripV2
            days={rollupQuery.data.rollup}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
        ) : null}
      </PortalDsCard>

      {!isLoading && !isError && (
        <>
          <PortalDsCard>
            <PortalDsSectionHeader
              title="Platform reconciliation"
              description="Vendor vs mart totals by platform, account, and metric."
            />
            <PlatformReconciliationPanelV2
              selectedDate={selectedDate}
              refreshToken={detailRefreshToken}
            />
          </PortalDsCard>

          <PortalDsCard>
            <PortalDsSectionHeader
              title="Mart verification"
              description="Mart-level verification checks by datasource and connector."
            />
            <MartVerificationPanelV2
              selectedDate={selectedDate}
              refreshToken={detailRefreshToken}
            />
          </PortalDsCard>
        </>
      )}
    </div>
  );
}
